import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Clock, CalendarDays, ArrowRight, PawPrint, Route as RouteIcon } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import ShareGuide from "@/components/ShareGuide";
import { WhereWeDrive } from "@/components/WhereWeDrive";

const STEPS = [
  ["Plan & quote", "Tell us who's moving — people, vehicle, pets, belongings. You get a clear estimate and a real plan, free."],
  ["We drive", "A background-checked, bilingual driver handles the highways, the border paperwork and the pet-friendly overnights."],
  ["Arrive settled", "Door-to-door delivery in Mérida, with arrival support for housing, groceries and getting oriented."],
];

export default function RouteGuide() {
  const { slug } = useParams();
  const [route, setRoute] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setRoute(null);
    setMissing(false);
    api.get(`/routes/${slug}`).then((r) => setRoute(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-32 text-center" data-testid="route-guide-missing">
        <h1 className="text-3xl font-light text-charcoal-900">We couldn't find that route</h1>
        <Link to="/drivers" className="mt-6 inline-block text-terracotta underline underline-offset-4">See everywhere we drive →</Link>
      </div>
    );
  }
  if (!route) return <div className="py-32 text-center text-charcoal-500">Loading…</div>;

  const isBorder = route.name.includes("Border");
  const title = isBorder ? `Cross at ${route.name} — We Drive You to Mérida` : `Move from ${route.name} to Mérida`;
  const stats = [
    [MapPin, `${(route.distance_km || 0).toLocaleString()} km`, "by road to Mérida"],
    [Clock, `~${route.drive_hours} hours`, "of driving, split into easy days"],
    [CalendarDays, `${route.trip_days} days`, "typical door-to-door trip"],
  ];

  return (
    <div data-testid="page-route-guide">
      <Seo title={title} path={`/routes/${slug}`}
        description={`Door-to-door driving and relocation service ${isBorder ? `via ${route.name}` : `from ${route.name}`} to Mérida, Mexico — ${route.distance_km} km, ~${route.drive_hours} hours. Background-checked bilingual drivers, pets welcome. Get an instant estimate.`} />

      <section className="bg-sand-100">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
          <div className="max-w-2xl animate-fade-up">
            <p className="text-sm uppercase tracking-[0.2em] text-clay-700 font-medium mb-4 inline-flex items-center gap-2">
              <RouteIcon size={14} /> Route guide · {route.region}
            </p>
            <h1 data-testid="route-guide-title" className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-charcoal-900 leading-[1.05]">
              {title}
            </h1>
            <p className="mt-6 text-lg text-charcoal-600 leading-relaxed">{route.guide_intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={`/cost-calculator?route=${encodeURIComponent(route.name)}`} data-testid="route-guide-estimate"
                className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3 text-sm text-white font-medium hover:bg-terracotta-hover transition-colors">
                Get my instant estimate <ArrowRight size={15} />
              </Link>
              <Link to="/book" data-testid="route-guide-book"
                className="inline-flex items-center gap-2 rounded-full border border-charcoal-300 px-7 py-3 text-sm text-charcoal-800 font-medium hover:bg-sand-200 transition-colors">
                Book a free call
              </Link>
              <ShareGuide slug={`route-${slug}`} title={title} />
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 space-y-16">
        <div className="grid gap-6 sm:grid-cols-3" data-testid="route-guide-stats">
          {stats.map(([Icon, big, small], i) => (
            <div key={i} className="rounded-3xl bg-white border border-sand-200 p-8">
              <Icon size={20} className="text-terracotta" />
              <p className="mt-3 font-serif text-3xl text-charcoal-800">{big}</p>
              <p className="mt-1 text-sm text-charcoal-500">{small}</p>
            </div>
          ))}
        </div>

        <div>
          <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-8">How your move works</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {STEPS.map(([t, d], i) => (
              <div key={i} className="rounded-3xl bg-sand-100 p-8">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-terracotta text-sm font-medium text-white">{i + 1}</span>
                <h3 className="mt-4 text-lg font-medium text-charcoal-800">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-charcoal-600">{d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl bg-sage-light p-8 lg:p-10 flex flex-wrap items-center gap-4" data-testid="route-guide-pets">
          <PawPrint size={24} className="text-sage-700 shrink-0" />
          <p className="text-charcoal-700 flex-1 min-w-[240px]">
            <span className="font-medium">Moving with pets?</span> They ride this route too — leash walks, pet-friendly
            hotels and photo updates at every stop.
          </p>
          <Link to="/pet-transport" className="text-sm font-medium text-terracotta underline underline-offset-4 hover:text-terracotta-hover">
            See the Pet Transport guide →
          </Link>
        </div>

        <WhereWeDrive />
      </div>
    </div>
  );
}
